<template>
  <div :class="cn('tw:block', $attrs.class as any)" :style="$attrs.style as any">
    <label v-if="label" :for="groupId" class="tw:mb-1.5 tw:block tw:text-13 tw:font-medium tw:text-gray-700">
      {{ label }}
      <span v-if="isRequired" aria-hidden="true" class="tw:text-red-600">&nbsp;*</span>
    </label>

    <div :id="groupId" role="group" class="tw:isolate tw:inline-flex tw:items-center">
      <LeadButton
        v-for="(option, index) in options"
        :key="String(option.value)"
        variant="white"
        :size="size"
        :icon="option.icon"
        :class="
          cn(
            'tw:rounded-none!',
            index === 0 && 'tw:rounded-l-md!',
            index === options.length - 1 && 'tw:rounded-r-md!',
            index > 0 && 'tw:-ml-px!',
            // pressed segment (DS pattern): primary-tint fill, primary text, no shadow, above neighbors
            isSelected(option.value) && 'tw:relative tw:z-10 tw:bg-primary-lt! tw:text-primary! tw:shadow-none!'
          )
        "
        :aria-pressed="isSelected(option.value)"
        :aria-label="option.ariaLabel ?? option.label"
        :data-testid="option.testId"
        @click="toggle(option.value)">
        <template v-if="option.label">{{ option.label }}</template>
      </LeadButton>
    </div>

    <span v-if="firstError" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-red-600">
      {{ firstError }}
    </span>
    <span v-else-if="$slots.hint" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-gray-600">
      <slot name="hint" />
    </span>
  </div>
</template>

<script lang="ts">
import type { Component } from 'vue';

export interface LeadSegmentedControlOption<V extends string | number = string | number> {
  value: V;
  label?: string;
  icon?: Component;
  ariaLabel?: string;
  testId?: string;
}
</script>

<script setup lang="ts" generic="T extends string | number">
// LeadSegmentedControl — the options-driven, form-scaffolded flavour of the
// DS <ButtonGroup>. Renders a row of joined buttons with radii collapsed
// except on the ends, borders overlapped via -ml-px, pressed state via
// aria-pressed + primary-lt tint. Supports radio / checkbox / exclusiveOption
// selection and label / errors / hint / required wrapping.
//
// For the raw DS <ButtonGroup> (a slot wrapper around plain <LeadButton>
// children — action groups, split buttons, menu caps) use LeadButtonGroup.
import { computed, useId } from 'vue';
import LeadButton, { type LeadButtonSize } from '@/components/lead-design/LeadButton.vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    options: LeadSegmentedControlOption<T>[];
    size?: LeadButtonSize;
    /** `radio` = single value (default). `checkbox` = array of values. */
    type?: 'radio' | 'checkbox';
    label?: string;
    errors?: string[];
    isRequired?: boolean;
    /**
     * Checkbox mode only. When this value is picked, all other selections clear;
     * when any other value is picked, this one clears. Use for "all of the above" /
     * "none of the above" style toggles.
     */
    exclusiveOption?: T;
  }>(),
  {
    size: 'md',
    type: 'radio',
    label: undefined,
    errors: () => [],
    isRequired: false,
    exclusiveOption: undefined,
  }
);

// v-model as T (radio) OR T[] (checkbox). One defineModel covers both because
// consumers wire it up per-mode; type is `any` for the internal ref to avoid
// forcing a generic union at the call site.
const modelValue = defineModel<T | T[]>({ required: true });

/* --------------------------------- COMPUTED ------------------------------- */

const generatedId = useId();
const groupId = computed(() => `lead-segmented-control-${generatedId}`);

const firstError = computed(() => props.errors?.[0] ?? '');

/* --------------------------------- METHODS -------------------------------- */

function isSelected(value: T): boolean {
  if (props.type === 'radio') {
    return modelValue.value === value;
  }
  return Array.isArray(modelValue.value) && (modelValue.value as T[]).includes(value);
}

function toggle(value: T) {
  if (props.type === 'radio') {
    modelValue.value = value;
    return;
  }

  const current = Array.isArray(modelValue.value) ? [...(modelValue.value as T[])] : [];

  if (props.exclusiveOption !== undefined) {
    if (value === props.exclusiveOption) {
      // toggling the exclusive option: either it becomes the only selection,
      // or (if already selected) we clear the group entirely.
      modelValue.value = current.includes(props.exclusiveOption) ? [] : [props.exclusiveOption];
      return;
    }
    // picking any non-exclusive value: drop the exclusive value from the set,
    // then toggle the picked value in/out.
    const withoutExclusive = current.filter((v) => v !== props.exclusiveOption);
    const idx = withoutExclusive.indexOf(value);
    if (idx === -1) withoutExclusive.push(value);
    else withoutExclusive.splice(idx, 1);
    modelValue.value = withoutExclusive;
    return;
  }

  const idx = current.indexOf(value);
  if (idx === -1) current.push(value);
  else current.splice(idx, 1);
  modelValue.value = current;
}
</script>
